import React from "react";
import * as LogRocket from "logrocket";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Plan } from "../../Customer";
import { APIIntercom } from "../../../../api";
import { useCustomerProvider } from "../../../../context";
import { ACCOUNTS_API_URLS } from "../../../../api/accounts";
import { getPlanPrice } from "../../../../components/ui/DiscountsModal/utils";
import { getNewPrice } from "../../../../components/ui/DiscountsModal/utils";
import { IPlan } from "../../../../api/plans/index.types";
import { IDiscountCodeForPlan } from "../../../../api/discounts/index.types";
import { Money } from "../../../../utils/Money";

interface IProps {
  editingPlan: Plan;
  editingPlanSaveStatus: string;
  setEditingPlan: (plan?: Plan) => void;
  setEditingPlanSaveStatus: (s: string) => void;
  editingPlanDiscountCode: IDiscountCodeForPlan | null;
}

const getPlanPriceWithDiscount = (
  plans: IPlan[],
  editingPlanDiscountCode: IDiscountCodeForPlan,
  editingPlan: Plan
) => {
  const newPrice = getNewPrice({
    plans,
    code: {
      // Handle the case when the discount code is assigned to All plans
      plan: editingPlanDiscountCode.plan_id
        ? Number(editingPlanDiscountCode.plan_id)
        : editingPlan.plans_id,
      amount: +editingPlanDiscountCode.amt,
      percent: editingPlanDiscountCode.type === "percent" ? true : false,
    },
  });

  const planPrice = Money.fromCents(getPlanPrice(plans, Number(editingPlanDiscountCode.plan_id)));
  const planPriceWithDiscount = planPrice - newPrice;

  return +(
    editingPlanDiscountCode.type === "percent"
      ? Money.toCents(newPrice)
      : Money.toCents(planPriceWithDiscount)
  ).toFixed(2);
};

const UpdatePlanConfirmWindow: React.FC<IProps> = ({
  editingPlan,
  editingPlanSaveStatus,
  setEditingPlan,
  setEditingPlanSaveStatus,
  editingPlanDiscountCode,
}) => {
  const params: { id: string } = useParams();
  const id = +params.id;
  const queryClient = useQueryClient();
  const plansData = useSelector((state) => state.plans);

  const { customer } = useCustomerProvider();
  const { data } = useSelector((state) => state.customer.plans);

  const current_edit_plan = data.plans.find(({ id }) => editingPlan.id === id);

  const isAddedDiscountCode = editingPlan.discount_code_id !== current_edit_plan?.discount_code_id;

  const saveEditingPlan = async () => {
    setEditingPlanSaveStatus("Please wait...");

    try {
      await APIIntercom.saveEditingPlanRequest(customer.xid, {
        id: editingPlan.id,
        expires: editingPlan.expires,
        previous_expires: current_edit_plan ? current_edit_plan.expires : editingPlan.expires,
        price_usd: Money.toCents(editingPlan.price_usd),
        previous_price_usd: current_edit_plan
          ? Money.toCents(current_edit_plan.price_usd)
          : Money.toCents(editingPlan.price_usd),
        status: editingPlan.status,
        accounts_id: customer.id,
        ...(isAddedDiscountCode &&
          editingPlanDiscountCode && {
            discount_code_id: editingPlanDiscountCode.id,
            price_usd: getPlanPriceWithDiscount(
              plansData.data.plans,
              editingPlanDiscountCode,
              editingPlan
            ),
          }),
      });

      setEditingPlanSaveStatus("Success!");
      LogRocket.track("Customer: Change Rebill Data");

      queryClient.invalidateQueries({
        queryKey: [ACCOUNTS_API_URLS.accountPlans, id, 0],
      });
      queryClient.invalidateQueries({
        queryKey: [ACCOUNTS_API_URLS.accountPlans],
      });
      setTimeout(() => {
        setEditingPlan();
      }, 2000);
    } catch (error) {
      setEditingPlanSaveStatus("Error!");
      console.error(error);
      setTimeout(() => {
        setEditingPlan();
      }, 2000);
    }
  };

  return (
    <div className="popup popup-save">
      <div className="title">Are you sure?</div>
      Are you sure you want to update this plan to the following?
      <br />
      <br />
      <b>Active: </b>
      {editingPlan.status === "active" ? "Yes" : "No"}
      <br />
      <b>Rebill Date: </b>
      {editingPlan.expires}
      <br />
      <b>Price: </b>$
      {isAddedDiscountCode && editingPlanDiscountCode
        ? Money.fromCents(
            getPlanPriceWithDiscount(plansData.data.plans, editingPlanDiscountCode, editingPlan)
          )
        : editingPlan.price_usd}
      <div style={{ padding: "10px 0" }}>{editingPlanSaveStatus}</div>
      <button type="button" onClick={() => setEditingPlan()}>
        Cancel
      </button>
      <button
        type="button"
        onClick={() => saveEditingPlan()}
        className="yes"
        data-test="button-save-intercom-profile"
      >
        Update
      </button>
    </div>
  );
};

export default UpdatePlanConfirmWindow;
